import PropTypes from 'prop-types';
import { useTranslation } from 'next-i18next';
import { Link } from 'next-translate-routes';
import Image from 'next/image';

// Utils
import { cn } from 'utils';

// Styles
import styles from './PresentationCarouselBlock.module.css';

// Components
import Tag from '../../atoms/Tag/Tag';
import Button from '../../atoms/Button/Button';
import CarouselBlock from '../../atoms/CarouselBlock/CarouselBlock';
import OpeningDateTag from '../OpeningDateTag/OpeningDateTag';
import { Picto } from '../../atoms/Picto/Picto';
import InfoModalButton from '../InfoModalButton/InfoModalButton';

function PresentationCarouselBlock({
  title,
  residence,
  address,
  adressMedia,
  freeText,
  freeMedia,
  phone,
  phoneMedia,
  ctaLabel,
  ctaLink,
  penetrationPricingDescription,
  penetrationPricing,
  penetrationPricingInfo,
  full,
  openingDate,
  theme,
  medias,
  className,
}) {
  const { t } = useTranslation();

  return (
    <div className={cn([styles.wrapper, styles[theme], className])}>
      <div className={styles.carousel}>
        <CarouselBlock
          medias={medias}
          theme={theme}
          size="compact"
        />
      </div>
      <div className={styles.container}>
        <div className={styles.tags}>
          {full ? (
            <Tag color="red" weight="light" content={full ? t('presentationBlock.full') : t('presentationBlock.open')} />
          ) : null}
          <OpeningDateTag openingDate={openingDate} />
        </div>

        <div className={styles.presentationBlocWrapper}>
          <div className={styles.presentationWrapper}>
            {residence ? (
              <h1 className={styles.title}>{title}</h1>
            ) : null}

            <div className={cn([styles.presentationContainer])}>
              <div className={styles.presentation}>
                {freeText ? (
                  <div className={cn([styles.freeTextContainer, styles.containerItem])}>
                    {freeMedia?.url ? (
                      <div className={cn([styles.freeTextImg, styles.imgItem])}>
                        <Image
                          src={freeMedia.url}
                          alt={freeMedia?.description}
                          fill
                          sizes="16vw"
                        />
                      </div>
                    ) : null}
                    <p className={styles.freeText}>{freeText}</p>
                  </div>
                ) : null}

                {address ? (
                  <div className={cn([styles.adressContainer, styles.containerItem])}>
                    {adressMedia?.url ? (
                      <div className={cn([styles.adressImg, styles.imgItem])}>
                        <Image
                          src={adressMedia.url}
                          alt={adressMedia?.description}
                          fill
                          sizes="16vw"
                        />
                      </div>
                    ) : null}
                    <a href="#map" className={cn([styles.adress, styles.underline])}>{address}</a>
                  </div>
                ) : null}

                {phone ? (
                  <div className={cn([styles.phoneContainer, styles.containerItem])}>
                    {phoneMedia?.url ? (
                      <div className={cn([styles.phoneImg, styles.imgItem])}>
                        <Image
                          src={phoneMedia.url}
                          alt={phoneMedia?.description}
                          fill
                          sizes="16vw"
                        />
                      </div>
                    ) : null}
                    <span className={cn([styles.phone])} aria-label={phone}>
                      {phone}
                    </span>
                  </div>
                ) : null}
              </div>

            </div>
          </div>
          <div className={styles.commercialInfos}>
            {penetrationPricingDescription
              ? <p className={styles.catchPhrase}>{penetrationPricingDescription}</p>
              : null}
            {penetrationPricing ? (
              <InfoModalButton
                id="penetrationPricingInfo"
                modalContent={<p>{penetrationPricingInfo}</p>}
              >
                <p className={styles.price}>
                  {penetrationPricing}
                </p>
                <Picto icon="information" className={styles.priceInfoIcon} />
              </InfoModalButton>
            ) : null}
            {ctaLabel && ctaLink ? (
              <Link href={ctaLink} name={ctaLabel}>
                <Button
                  CTAType="secondary"
                  theme={theme}
                  usageContext={theme}
                  outline
                  className={styles.commercialBtn}
                >
                  {ctaLabel}
                </Button>
              </Link>
            ) : null}
          </div>
        </div>

      </div>
    </div>
  );
}

PresentationCarouselBlock.propTypes = {
  title: PropTypes.string.isRequired,
  medias: PropTypes.arrayOf(PropTypes.shape({
    url: PropTypes.string,
    description: PropTypes.string,
    credit: PropTypes.string,
  })),
  residence: PropTypes.string,
  address: PropTypes.string,
  adressMedia: PropTypes.string,
  freeText: PropTypes.string,
  freeMedia: PropTypes.string,
  phone: PropTypes.string,
  phoneMedia: PropTypes.string,
  ctaLabel: PropTypes.string,
  ctaLink: PropTypes.string,
  penetrationPricingDescription: PropTypes.string,
  penetrationPricing: PropTypes.string,
  penetrationPricingInfo: PropTypes.string,
  full: PropTypes.bool,
  openingDate: PropTypes.string,
  theme: PropTypes.oneOf(['light', 'dark', 'neutral', '']),
  className: PropTypes.string,
};

PresentationCarouselBlock.defaultProps = {
  medias: [],
  residence: '',
  address: '',
  adressMedia: '',
  freeText: '',
  freeMedia: '',
  phone: '',
  phoneMedia: '',
  ctaLabel: '',
  ctaLink: '',
  penetrationPricingDescription: '',
  penetrationPricing: '',
  penetrationPricingInfo: '',
  full: false,
  openingDate: '',
  theme: 'light',
  className: '',
};

export default PresentationCarouselBlock;
